import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Bot, Check, Loader2, Pencil } from "lucide-react";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { PROVIDERS, type ProviderId } from "~/lib/onboarding/constants";
import {
  $getUserProviders,
  $saveProviderConnection,
  $updateOnboardingStep,
  $updateProviderConnection,
} from "~/lib/onboarding/mutations";
import { getString, isRecord } from "~/lib/type-guards";

type DeviceCodeData = {
  device_code: string;
  user_code: string;
  verification_uri: string;
  expires_in: number;
  interval: number;
};

type PollTokenData = {
  access_token: string;
};

type ErrorResponse = {
  error: string;
};

type SaveConnectionResponse = {
  connectionId: string;
  isNew: boolean;
};

function ProviderSelectionScreen() {
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState<ProviderId | null>(null);
  const [editingConnectionId, setEditingConnectionId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [githubOAuthState, setGithubOAuthState] = useState<{
    userCode: string;
    verificationUri: string;
    deviceCode: string;
    interval: number;
  } | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const { data: userProviders, refetch } = useSuspenseQuery({
    queryKey: ["userProviders"],
    queryFn: () => $getUserProviders(),
  });

  const provider = PROVIDERS.find((p) => p.id === selectedProvider);
  const hasProviders = userProviders.length > 0;
  const addedProviderIds = new Set(userProviders.map((p) => p.provider));
  const hasDefaultProvider = userProviders.some((p) => p.isDefault);
  const isEditMode = editingConnectionId !== null;

  const handleProviderClick = (providerId: ProviderId) => {
    const providerConfig = PROVIDERS.find((p) => p.id === providerId);
    if (providerConfig?.isSystemManaged) {
      setError("QuickPrompt Free Tier is managed by QuickPrompt.");
      return;
    }

    const existingProvider = userProviders.find((p) => p.provider === providerId);

    if (existingProvider) {
      // Edit existing provider
      setEditingConnectionId(existingProvider.id);
      setSelectedProvider(providerId);
      setDisplayName(existingProvider.displayName);
      setApiKey("");
      setIsDefault(existingProvider.isDefault);
      setShowAddForm(true);
    } else {
      // Add new provider
      setEditingConnectionId(null);
      setSelectedProvider(providerId);
      setDisplayName(providerConfig?.name || "");
      setApiKey("");
      setIsDefault(!hasDefaultProvider);
      setShowAddForm(true);
    }
    setError(null);
  };

  const handleCancelAdd = () => {
    setSelectedProvider(null);
    setEditingConnectionId(null);
    setShowAddForm(false);
    setDisplayName("");
    setApiKey("");
    setError(null);
    setGithubOAuthState(null);
    setIsPolling(false);
  };

  const saveMutation = useMutation({
    mutationFn: async (data: {
      provider: ProviderId;
      displayName: string;
      apiKey?: string;
      isDefault: boolean;
    }) => {
      const connectionId = await $saveProviderConnection({ data });
      return connectionId;
    },
    onSuccess: async (connectionId: string) => {
      await refetch();
      navigate({
        to: "/onboarding/test-connection",
        search: { connectionId },
      });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to save provider");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: {
      connectionId: string;
      displayName?: string;
      apiKey?: string;
      isDefault?: boolean;
    }) => {
      await $updateProviderConnection({ data });
    },
    onSuccess: async () => {
      await refetch();
      handleCancelAdd();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to update provider");
    },
  });

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!selectedProvider || !displayName) return;

    // GitHub Copilot OAuth flow
    if (selectedProvider === "github-copilot" && !isEditMode) {
      try {
        setError(null);
        setIsPolling(true);

        // Step 1: Request device code
        const deviceCodeResponse = await fetch("/api/providers?action=request-code");
        if (!deviceCodeResponse.ok) {
          throw new Error("Failed to request device code");
        }
        const deviceCodeUnknown: unknown = await deviceCodeResponse.json();
        if (!isRecord(deviceCodeUnknown)) {
          throw new Error("Invalid device code response");
        }
        const deviceCodeData: DeviceCodeData = {
          device_code: getString(deviceCodeUnknown, "device_code") ?? "",
          user_code: getString(deviceCodeUnknown, "user_code") ?? "",
          verification_uri: getString(deviceCodeUnknown, "verification_uri") ?? "",
          expires_in:
            typeof deviceCodeUnknown.expires_in === "number"
              ? deviceCodeUnknown.expires_in
              : 0,
          interval:
            typeof deviceCodeUnknown.interval === "number"
              ? deviceCodeUnknown.interval
              : 0,
        };
        if (
          !deviceCodeData.user_code ||
          !deviceCodeData.verification_uri ||
          !deviceCodeData.device_code ||
          !deviceCodeData.interval
        ) {
          throw new Error("Invalid device code response");
        }

        setGithubOAuthState({
          userCode: deviceCodeData.user_code,
          verificationUri: deviceCodeData.verification_uri,
          deviceCode: deviceCodeData.device_code,
          interval: deviceCodeData.interval,
        });

        // Step 2: Poll for access token
        const pollResponse = await fetch(
          `/api/providers?action=poll-token&device_code=${deviceCodeData.device_code}&interval=${deviceCodeData.interval}`,
        );

        if (!pollResponse.ok) {
          const errorUnknown: unknown = await pollResponse.json();
          const errorMessage =
            isRecord(errorUnknown) && getString(errorUnknown, "error")
              ? (errorUnknown as ErrorResponse).error
              : undefined;
          throw new Error(errorMessage || "Failed to authorize with GitHub");
        }

        const tokenUnknown: unknown = await pollResponse.json();
        if (!isRecord(tokenUnknown)) {
          throw new Error("Invalid token response");
        }
        const tokenData: PollTokenData = {
          access_token: getString(tokenUnknown, "access_token") ?? "",
        };
        if (!tokenData.access_token) {
          throw new Error("Invalid token response");
        }

        // Step 3: Save connection with GitHub token
        const saveResponse = await fetch("/api/providers?action=save-connection", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            githubAccessToken: tokenData.access_token,
          }),
        });

        if (!saveResponse.ok) {
          const errorUnknown: unknown = await saveResponse.json();
          const errorMessage =
            isRecord(errorUnknown) && getString(errorUnknown, "error")
              ? (errorUnknown as ErrorResponse).error
              : undefined;
          throw new Error(errorMessage || "Failed to save GitHub Copilot connection");
        }

        const saveUnknown: unknown = await saveResponse.json();
        if (!isRecord(saveUnknown)) {
          throw new Error("Invalid save response");
        }
        const saveData: SaveConnectionResponse = {
          connectionId: getString(saveUnknown, "connectionId") ?? "",
          isNew: typeof saveUnknown.isNew === "boolean" ? saveUnknown.isNew : false,
        };
        if (!saveData.connectionId) {
          throw new Error("Invalid save response");
        }

        // If isDefault is checked, update the connection
        if (isDefault) {
          await $updateProviderConnection({
            data: {
              connectionId: saveData.connectionId,
              isDefault: true,
            },
          });
        }

        await refetch();
        navigate({
          to: "/onboarding/test-connection",
          search: { connectionId: saveData.connectionId },
        });
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to connect GitHub Copilot");
        setIsPolling(false);
        setGithubOAuthState(null);
      }
      return;
    }

    if (isEditMode) {
      // Update existing provider
      const updates: {
        connectionId: string;
        displayName?: string;
        apiKey?: string;
        isDefault?: boolean;
      } = {
        connectionId: editingConnectionId!,
        displayName,
        isDefault,
      };

      if (apiKey) {
        updates.apiKey = apiKey;
      }

      updateMutation.mutate(updates);
    } else {
      // Add new provider
      if (provider?.requiresApiKey && !apiKey) {
        setError("API key is required");
        return;
      }

      saveMutation.mutate({
        provider: selectedProvider,
        displayName,
        apiKey: apiKey || undefined,
        isDefault,
      });
    }
  };

  const handleContinue = async () => {
    if (!hasProviders) {
      setError("Please add at least one provider to continue");
      return;
    }

    if (!hasDefaultProvider) {
      setError("Please set at least one provider as default");
      return;
    }

    await $updateOnboardingStep({ data: { step: 2 } });
    const defaultProvider = userProviders.find((p) => p.isDefault);
    navigate({
      to: "/onboarding/templates",
      search: { connectionId: defaultProvider!.id },
    });
  };

  return (
    <div className="flex flex-col items-center">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold tracking-tight">
          {hasProviders ? "Your AI Providers" : "Choose your AI Provider"}
        </h1>
        <p className="text-muted-foreground">
          {hasProviders
            ? "Add more providers or continue to the next step"
            : "Select a provider and enter your API key to get started"}
        </p>
      </div>

      {/* Provider Cards */}
      {!showAddForm && (
        <>
          <div className="mb-8 grid w-full grid-cols-2 gap-4">
            {PROVIDERS.map((p) => {
              const isAdded = addedProviderIds.has(p.id);

              return (
                <Card
                  key={p.id}
                  className={`group relative cursor-pointer transition-all ${
                    isAdded
                      ? "border-green-500/50 bg-green-50/50 hover:border-green-500 dark:bg-green-950/20"
                      : "hover:border-primary hover:bg-accent/30"
                  }`}
                  onClick={() => handleProviderClick(p.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="bg-muted mb-3 flex size-10 items-center justify-center rounded-lg">
                        <Bot className="text-foreground size-5" />
                      </div>
                      {isAdded && (
                        <div className="relative flex size-6 items-center justify-center">
                          <div className="absolute flex size-6 items-center justify-center rounded-full bg-green-500 transition-opacity group-hover:opacity-0">
                            <Check className="size-4 text-white" />
                          </div>
                          <div className="absolute flex size-6 items-center justify-center rounded-full bg-green-500 opacity-0 transition-opacity group-hover:opacity-100">
                            <Pencil className="size-3.5 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    <CardTitle className="text-lg">{p.name}</CardTitle>
                    <CardDescription>
                      {isAdded
                        ? p.isSystemManaged
                          ? "Managed by QuickPrompt"
                          : "Connected • Click to edit"
                        : p.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="w-full max-w-md space-y-3">
            {error && <p className="text-destructive text-center text-sm">{error}</p>}

            {hasProviders ? (
              <>
                <Button onClick={handleContinue} className="w-full" size="lg">
                  Continue to Templates
                </Button>
                <p className="text-muted-foreground text-center text-sm">
                  {userProviders.length} provider{userProviders.length !== 1 ? "s" : ""}{" "}
                  configured
                </p>
              </>
            ) : (
              <p className="text-muted-foreground text-center text-sm">
                Click on a provider above to get started
              </p>
            )}
          </div>
        </>
      )}

      {/* Add/Edit Provider Form */}
      {showAddForm && selectedProvider && (
        <form onSubmit={handleSubmit} className="w-full max-w-md space-y-4">
          <div className="mb-4 text-center">
            <h2 className="text-xl font-semibold">
              {isEditMode ? "Edit" : "Add"} {provider?.name}
            </h2>
            <p className="text-muted-foreground text-sm">
              {isEditMode
                ? "Update your provider credentials"
                : "Enter your credentials to connect"}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={`My ${provider?.name} Account`}
              autoFocus
            />
          </div>

          {provider?.requiresApiKey && (
            <div className="space-y-2">
              <Label htmlFor="apiKey">
                API Key {isEditMode && "(leave blank to keep current)"}
              </Label>
              <Input
                id="apiKey"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={
                  isEditMode
                    ? "Enter new API key (optional)"
                    : selectedProvider === "openai"
                      ? "sk-..."
                      : selectedProvider === "anthropic"
                        ? "sk-ant-..."
                        : "sk-or-..."
                }
              />
            </div>
          )}

          {selectedProvider === "github-copilot" && !isEditMode && (
            <>
              {githubOAuthState ? (
                <div className="bg-primary/10 rounded-lg border p-4">
                  <p className="mb-3 text-sm font-semibold">Authorize GitHub Copilot</p>
                  <p className="text-muted-foreground mb-2 text-sm">
                    Visit the following URL and enter the code:
                  </p>
                  <div className="bg-background mb-3 rounded border p-3">
                    <p className="mb-2 text-sm">
                      <strong>URL:</strong>{" "}
                      <a
                        href={githubOAuthState.verificationUri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {githubOAuthState.verificationUri}
                      </a>
                    </p>
                    <p className="text-sm">
                      <strong>Code:</strong>{" "}
                      <span className="font-mono text-lg">
                        {githubOAuthState.userCode}
                      </span>
                    </p>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    Waiting for authorization...
                  </p>
                </div>
              ) : (
                <div className="bg-primary/10 rounded-lg border p-4">
                  <p className="text-muted-foreground mb-2 text-sm">
                    Click "Connect" below to start the GitHub authorization process.
                    You'll receive a code to enter on GitHub's authorization page.
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Note: You need an active GitHub Copilot subscription to use this
                    provider.
                  </p>
                </div>
              )}
            </>
          )}

          {!hasDefaultProvider && !isEditMode && (
            <div className="bg-primary/10 rounded-lg border p-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="isDefault"
                  checked={isDefault}
                  onCheckedChange={(checked) => setIsDefault(checked === true)}
                  disabled
                />
                <Label htmlFor="isDefault" className="text-sm">
                  Set as default provider (required for first provider)
                </Label>
              </div>
            </div>
          )}

          {(hasDefaultProvider || isEditMode) && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="isDefault"
                checked={isDefault}
                onCheckedChange={(checked) => setIsDefault(checked === true)}
              />
              <Label htmlFor="isDefault" className="cursor-pointer text-sm">
                Set as default provider
              </Label>
            </div>
          )}

          {error && <p className="text-destructive text-sm">{error}</p>}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCancelAdd}
              disabled={saveMutation.isPending || updateMutation.isPending || isPolling}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={
                saveMutation.isPending ||
                updateMutation.isPending ||
                isPolling ||
                !displayName ||
                (!isEditMode && provider?.requiresApiKey && !apiKey)
              }
            >
              {saveMutation.isPending || updateMutation.isPending || isPolling ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {isPolling
                    ? "Connecting..."
                    : saveMutation.isPending || updateMutation.isPending
                      ? "Saving..."
                      : ""}
                </>
              ) : isEditMode ? (
                "Update Provider"
              ) : selectedProvider === "github-copilot" ? (
                "Connect with GitHub"
              ) : (
                "Save & Test"
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export const Route = createFileRoute("/(authenticated)/onboarding/provider")({
  component: ProviderSelectionScreen,
});
